import React, { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import * as Sentry from "@sentry/react-native";

import OnboardingBaseScreen from "./OnboardingBaseScreen";
import Interest from "./Attributes/Interest";
import Language from "./Attributes/Language";

import type { AppUserSession } from "@/schemas/auth/session";
import { Spacings } from "@/styling/spacings";
import { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import type { AttributeValue } from "@/schemas/attribute/AttributeSet";
import NoteCard from "@/Components/Cards/NoteCard";

type Props = {
  session: AppUserSession;
  title: string;
  description: string;
  userAttributeType: UserAttributeType;
};

function OnboardingTagSelectionScreen({
  session,
  userAttributeType,
  title,
  description,
}: Props): React.JSX.Element {
  const [attributeSelections, setAttributeSelections] = useState<
    AttributeValue[]
  >([]);

  const getAttributeComponent = (): React.JSX.Element => {
    switch (userAttributeType) {
      case UserAttributeType.INTEREST:
        return (
          <Interest
            onSelectionChange={(values: AttributeValue[]) =>
              setAttributeSelections(values)
            }
            selectedValues={attributeSelections}
          />
        );
      case UserAttributeType.LANGUAGES:
        return (
          <Language
            onSelectionChange={(values: AttributeValue[]) =>
              setAttributeSelections(values)
            }
            selectedValues={attributeSelections}
          />
        );
      default:
        Sentry.captureMessage("Unknown user attribute type");
        return <></>;
    }
  };

  const stageMainComponent: React.JSX.Element = (
    <View style={styles.sectionContainer}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <NoteCard text={description} />
      {getAttributeComponent()}
    </View>
  );

  return (
    <OnboardingBaseScreen
      session={session}
      title={"Build Your Profile"}
      subtitle="Let's get your profile set up so you can start meeting amazing
          people."
      canContinue={attributeSelections.length > 0}
      stageMainComponent={stageMainComponent}
      attributeType={userAttributeType}
      attributeSelections={attributeSelections}
    />
  );
}

const styles = StyleSheet.create({
  sectionContainer: {
    flexDirection: "column",
    alignItems: "center",
    marginBottom: Spacings.s3,
    marginHorizontal: Spacings.s3,
    flex: 1,
  },
  sectionTitle: {
    fontSize: 20,
    marginBottom: Spacings.s3,
    fontWeight: "bold",
  },
});

export default OnboardingTagSelectionScreen;
